import { useState, useEffect } from 'react';
import { Wallet, ArrowDownLeft, ArrowUpRight, RefreshCw, TrendingUp, TrendingDown, CreditCard, Building, Key, Receipt, Gift } from 'lucide-react';
import { PageShell, StatCard, fmtINR, fmtDate, Spinner, useBanner, useWalletUser, EmptyState } from './WalletShared';
import walletController from '../../controllers/walletController';

const WalletOverview = () => {
  const { userId } = useWalletUser();
  const { banner, notify, clearBanner } = useBanner();
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent=false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    const res = await walletController.getWalletOverview(userId);
    if (res.success) setData(res.data);
    else notify('error', res.message);
    setLoading(false); setRefreshing(false);
  };

  useEffect(() => { load(false); }, [userId]);

  const TXN_COLORS = { credit:{ color:'#15803d', bg:'#dcfce7', sign:'+' }, debit:{ color:'#b91c1c', bg:'#fef2f2', sign:'-' } };

  if (loading) return <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'60vh', gap:10, color:'#64748b' }}><Spinner size={28}/><span style={{ fontSize:14 }}>Loading wallet…</span></div>;

  return (
    <PageShell title="Wallet Overview" subtitle="Your wallet summary at a glance" icon={Wallet}
      banner={banner} onCloseBanner={clearBanner}
      actions={
        <button onClick={() => load(true)} disabled={refreshing}
          style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 12px', background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, fontSize:12, fontWeight:500, cursor:'pointer', color:'#475569' }}>
          <RefreshCw size={13} style={{ animation:refreshing?'spin 1s linear infinite':'none' }}/> Refresh
        </button>
      }>

      {/* Balance hero */}
      <div style={{ background:'linear-gradient(135deg,#1e293b 0%,#334155 100%)', borderRadius:14, padding:'28px 24px', marginBottom:20, color:'#fff' }}>
        <div style={{ fontSize:12, color:'#94a3b8', marginBottom:6, letterSpacing:'0.5px', textTransform:'uppercase' }}>Available Balance</div>
        <div style={{ fontSize:42, fontWeight:700, marginBottom:8 }}>{fmtINR(data?.balance?.available||0)}</div>
        <div style={{ display:'flex', gap:24, fontSize:13 }}>
          <div><span style={{ color:'#94a3b8' }}>Pending: </span><span style={{ color:'#fbbf24' }}>{fmtINR(data?.balance?.pending||0)}</span></div>
          <div><span style={{ color:'#94a3b8' }}>Locked: </span><span style={{ color:'#f87171' }}>{fmtINR(data?.balance?.locked||0)}</span></div>
          <div><span style={{ color:'#94a3b8' }}>Cashback: </span><span style={{ color:'#34d399' }}>{fmtINR(data?.balance?.cashback||0)}</span></div>
        </div>
      </div>

      {/* Stats grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:12, marginBottom:20 }}>
        <StatCard label="Total In"       value={fmtINR(data?.stats?.totalIn||0)}       icon={ArrowDownLeft} color="#15803d"/>
        <StatCard label="Total Out"      value={fmtINR(data?.stats?.totalOut||0)}      icon={ArrowUpRight}  color="#b91c1c"/>
        <StatCard label="This Month"     value={fmtINR(data?.stats?.thisMonth||0)}     icon={TrendingUp}    color="#1d4ed8"/>
        <StatCard label="Reward Points"  value={data?.rewards?.points||0}              icon={Gift}          color="#92400e"/>
        <StatCard label="Active Keys"    value={data?.keys?.active||0}                 icon={Key}           color="#0f6e56"/>
        <StatCard label="Open Invoices"  value={data?.invoices?.open||0}               icon={Receipt}       color="#6d28d9"/>
      </div>

      {/* Recent transactions */}
      <div style={{ background:'#fff', borderRadius:12, border:'1px solid #f1f5f9', overflow:'hidden' }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:14, fontWeight:700, color:'#1e293b' }}>Recent Transactions</span>
          <a href="/dashboard/wallet/transactions" style={{ fontSize:12, color:'#3b82f6', textDecoration:'none' }}>View all →</a>
        </div>
        {!data?.recentTransactions?.length
          ? <EmptyState icon={Wallet} message="No transactions yet"/>
          : (data.recentTransactions||[]).slice(0,8).map((t,i) => {
              const cfg = TXN_COLORS[t.type] || TXN_COLORS.debit;
              return (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 18px', borderBottom:'1px solid #f8fafc' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:34, height:34, borderRadius:8, background:cfg.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {t.type==='credit' ? <ArrowDownLeft size={15} color={cfg.color}/> : <ArrowUpRight size={15} color={cfg.color}/>}
                    </div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:500, color:'#1e293b' }}>{t.description||t.type}</div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>{fmtDate(t.createdAt)}</div>
                    </div>
                  </div>
                  <span style={{ fontSize:14, fontWeight:700, color:cfg.color }}>{cfg.sign}{fmtINR(t.amount)}</span>
                </div>
              );
            })}
      </div>
    </PageShell>
  );
};
export default WalletOverview;